// The About page editor's session draft (plan R6 AB-1): while there are unsaved
// changes they are kept in sessionStorage (debounced), so a reload or a forced
// sign-in can bring them back. Every storage access is guarded: a blocked or
// full storage only loses the draft, never the editor.
import { useCallback, useEffect } from 'react';
import { AboutDraft, AboutSection, DRAFT_KEY, makeDraft, parseDraft } from './aboutModel';

const DEBOUNCE_MS = 500;

export function readDraft(): AboutDraft | null {
  try {
    return parseDraft(window.sessionStorage.getItem(DRAFT_KEY));
  } catch {
    return null;
  }
}

export function clearDraft(): void {
  try {
    window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // storage blocked: nothing to clear
  }
}

type Options = {
  // False until the page loaded and any restore offer was answered.
  enabled: boolean;
  dirty: boolean;
  baseUpdatedAt: string | null | undefined;
  sections: AboutSection[];
};

export default function useAboutDraft({ enabled, dirty, baseUpdatedAt, sections }: Options) {
  useEffect(() => {
    if (!enabled) return undefined;
    if (!dirty) {
      clearDraft();
      return undefined;
    }
    const t = window.setTimeout(() => {
      try {
        window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(makeDraft(baseUpdatedAt, sections)));
      } catch {
        // storage blocked or full: the editor keeps working without a draft
      }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [enabled, dirty, baseUpdatedAt, sections]);

  return { read: useCallback(readDraft, []), clear: useCallback(clearDraft, []) };
}
